/**
 * Canonical data structures shared by every module in Lunchly.
 *
 * There are only five shapes in the whole application:
 *
 *   Rota        – what the manager typed/imported. Raw strings, one row per person.
 *   ShiftRecord – one normalised, parsed shift for one person on one day.
 *   StaffRule   – a persistent per-employee override (confirmed exceptions live here).
 *   Assignment  – "this person takes lunch at this time on this day".
 *   Schedule    – a week of assignments plus coverage, warnings and explanations.
 *
 * Everything else in the codebase is a transformation between these five.
 */

import {
  DAY_KEYS,
  classifyShiftCell,
  dateForDayKey,
  formatMinutes,
  parseShiftText,
  parseTimeToMinutes,
} from './time.js';
import { employeeIdFromName, firstNameOf, slugify } from './ids.js';

export const ROTA_SCHEMA_VERSION = 1;

/* -------------------------------------------------------------------------- */
/* Rota                                                                       */
/* -------------------------------------------------------------------------- */

/** Create an empty rota row. */
export function createRotaRow(name = '', cells = {}) {
  const row = {
    employeeId: name ? employeeIdFromName(name) : '',
    name,
    cells: {},
  };
  for (const dayKey of DAY_KEYS) {
    row.cells[dayKey] = cells[dayKey] ?? '';
  }
  return row;
}

/** Create an empty rota for a given week-commencing date. */
export function createRota(weekCommencing, rows = [], meta = {}) {
  return {
    schemaVersion: ROTA_SCHEMA_VERSION,
    weekCommencing,
    source: meta.source ?? 'manual',
    importedAt: meta.importedAt ?? null,
    notes: meta.notes ?? '',
    rows: rows.map((row) => createRotaRow(row.name, row.cells ?? cellsFromArray(row.shifts))),
  };
}

/** Convert a 7-element array of raw cell strings into a keyed cell object. */
export function cellsFromArray(shifts = []) {
  const cells = {};
  DAY_KEYS.forEach((dayKey, index) => {
    cells[dayKey] = shifts[index] ?? '';
  });
  return cells;
}

/**
 * Re-derive employee ids and de-duplicate rows.
 * Two rows carrying the same name are kept (so the manager can see and fix the
 * mistake) but the duplicate is marked so validation can warn about it.
 */
export function normaliseRota(rota) {
  const weekCommencing = rota?.weekCommencing ?? '';
  const seen = new Map();
  const rows = (rota?.rows ?? []).map((row, index) => {
    const name = String(row?.name ?? '').trim();
    const employeeId = name ? employeeIdFromName(name, index) : `employee-${index + 1}`;
    const count = (seen.get(employeeId) ?? 0) + 1;
    seen.set(employeeId, count);
    const cells = {};
    for (const dayKey of DAY_KEYS) {
      cells[dayKey] = String(row?.cells?.[dayKey] ?? '').trim();
    }
    return {
      employeeId,
      name,
      cells,
      duplicateOf: count > 1 ? employeeId : null,
      duplicateIndex: count,
    };
  });

  return {
    schemaVersion: ROTA_SCHEMA_VERSION,
    weekCommencing,
    source: rota?.source ?? 'manual',
    importedAt: rota?.importedAt ?? null,
    notes: rota?.notes ?? '',
    rows,
  };
}

/** True when a rota contains no rows with any content at all. */
export function isRotaEmpty(rota) {
  if (!rota?.rows?.length) return true;
  return rota.rows.every(
    (row) => !row.name?.trim() && DAY_KEYS.every((dayKey) => !row.cells?.[dayKey]?.trim()),
  );
}

/* -------------------------------------------------------------------------- */
/* ShiftRecord                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Turn one rota cell into a ShiftRecord.
 *
 * `kind` is one of 'shift' | 'absent' | 'blank' | 'unparsed'. Only 'shift'
 * carries time fields; the others exist so the UI can show *why* somebody is
 * not being considered rather than silently dropping them.
 */
export function buildShiftRecord({ employeeId, name, dayKey, weekCommencing, raw, settings }) {
  const classified = classifyShiftCell(raw);
  const base = {
    employeeId,
    name,
    dayKey,
    date: weekCommencing ? dateForDayKey(weekCommencing, dayKey) : '',
    kind: classified.kind,
    raw: classified.raw,
    startMinutes: null,
    endMinutes: null,
    durationMinutes: 0,
    crossesMidnight: false,
    isOvernight: false,
    parseReason: classified.reason ?? null,
  };

  if (classified.kind !== 'shift') return base;

  const threshold = Number(settings?.overnightEndThresholdMinutes ?? 1440);
  const { startMinutes, endMinutes, durationMinutes, crossesMidnight } = classified.shift;

  return {
    ...base,
    startMinutes,
    endMinutes,
    durationMinutes,
    crossesMidnight,
    // A 16:00-00:00 shift finishes *at* midnight and is an ordinary evening
    // shift. Only shifts finishing strictly after the threshold are overnight.
    isOvernight: endMinutes > threshold,
  };
}

/** Build every ShiftRecord for one day. */
export function buildDayShifts(rota, dayKey, settings) {
  const normalised = normaliseRota(rota);
  return normalised.rows
    .filter((row) => row.name)
    .map((row) =>
      buildShiftRecord({
        employeeId: row.employeeId,
        name: row.name,
        dayKey,
        weekCommencing: normalised.weekCommencing,
        raw: row.cells[dayKey],
        settings,
      }),
    );
}

/** Build ShiftRecords for the whole week, keyed by day. */
export function buildWeekShifts(rota, settings) {
  const week = {};
  for (const dayKey of DAY_KEYS) {
    week[dayKey] = buildDayShifts(rota, dayKey, settings);
  }
  return week;
}

/** Only the records that describe an actual worked shift. */
export function workingShifts(records) {
  return records.filter((record) => record.kind === 'shift');
}

/* -------------------------------------------------------------------------- */
/* StaffRule                                                                  */
/* -------------------------------------------------------------------------- */

export const PRIORITIES = Object.freeze(['early', 'balanced', 'late']);

/**
 * The persistent employee override object.
 *
 * Alicia's exception is expressed purely as data:
 *   { employeeId: 'alicia-brennan', forceLunchMinimumMinutes: 360,
 *     preferredLunchTime: '12:30', priority: 'early', allowLaterWhenBusy: true }
 *
 * The scheduler never checks a name - it only reads these fields - so managers
 * can create the same exception for anybody else from the Staff rules panel.
 */
export function createStaffRule(partial = {}) {
  const employeeId = partial.employeeId
    ? slugify(partial.employeeId)
    : employeeIdFromName(partial.name ?? '');
  return {
    employeeId,
    /** Display label for the rule list; not the export alias. */
    label: partial.label ?? partial.name ?? employeeId,
    /** Never schedule a lunch for this person (e.g. they arrange their own). */
    excludeFromLunch: partial.excludeFromLunch === true,
    /** Grant a lunch whenever the shift is at least this many minutes long. */
    forceLunchMinimumMinutes: toNullableNumber(partial.forceLunchMinimumMinutes),
    /** Ideal clock time, e.g. "12:30". Beats the generic shift-offset target. */
    preferredLunchTime: partial.preferredLunchTime || null,
    /** Optional day-specific ideal times, e.g. `{ tue: "13:30" }`. */
    preferredLunchTimesByDay: normalisePreferredTimesByDay(partial.preferredLunchTimesByDay),
    /** Optional hard clock-time bounds for the lunch start, e.g. 15:00–15:30. */
    earliestLunchTime: partial.earliestLunchTime || null,
    latestLunchTime: partial.latestLunchTime || null,
    /**
     * Shift patterns this rule's clock times apply to, e.g. `['09:00-21:00']`.
     *
     * These rules are agreed about a *shift*, not about a person in general -
     * "Rachel works 09:00-21:00 on Monday and should take lunch around 14:30".
     * Left empty the rule applies to every shift, which is right for somebody
     * like Rebekah whose note says "whenever she is working", and wrong for a
     * rule pinned to one pattern: Rachel's 14:30 was landing on her 07:00-15:00
     * Saturday, where it clamps to 13:30 with an hour of the shift left.
     */
    appliesToShifts: normaliseShiftPatterns(partial.appliesToShifts),
    /** 'early' | 'balanced' | 'late' - biases the scoring, never forces it. */
    priority: PRIORITIES.includes(partial.priority) ? partial.priority : 'balanced',
    /** May the scheduler push this person later when coverage demands it? */
    allowLaterWhenBusy: partial.allowLaterWhenBusy !== false,
    /** Pin somebody to the day/evening/night group regardless of shift times. */
    lunchGroupOverride: partial.lunchGroupOverride || null,
    /** Optional per-employee replacements for the day profile timings. */
    minMinutesAfterShiftStart: toNullableNumber(partial.minMinutesAfterShiftStart),
    minMinutesBeforeShiftEnd: toNullableNumber(partial.minMinutesBeforeShiftEnd),
    /** Free-text justification shown in the explain panel. */
    notes: partial.notes ?? '',
    /** Built-in seeds are marked so a reset can restore them. */
    seeded: partial.seeded === true,
  };
}

function toNullableNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function normalisePreferredTimesByDay(value) {
  const result = {};
  if (!value || typeof value !== 'object' || Array.isArray(value)) return result;
  for (const dayKey of DAY_KEYS) {
    const clock = String(value[dayKey] ?? '').trim();
    if (Number.isFinite(parseTimeToMinutes(clock))) result[dayKey] = clock;
  }
  return result;
}

/** Index an array of staff rules by employee id. */
export function indexStaffRules(rules = []) {
  const map = new Map();
  for (const rule of rules) {
    const normalised = createStaffRule(rule);
    if (normalised.employeeId) map.set(normalised.employeeId, normalised);
  }
  return map;
}

/** Look up a rule, returning an inert default when the employee has none. */
export function ruleFor(rulesIndex, employeeId) {
  return rulesIndex?.get?.(employeeId) ?? createStaffRule({ employeeId });
}

/** Resolve a rule's preferred clock time into minutes, or null. */
export function preferredTimeMinutes(rule, dayKey = null) {
  const dayPreference = dayKey ? rule?.preferredLunchTimesByDay?.[dayKey] : null;
  const value = dayPreference || rule?.preferredLunchTime;
  return value ? parseTimeToMinutes(value) : null;
}

/**
 * Canonicalise `appliesToShifts` entries to "HH:MM-HH:MM" so a rule written as
 * "9:00 - 21:00" still matches a shift parsed from "09:00-21:00".
 */
function normaliseShiftPatterns(value) {
  const list = Array.isArray(value) ? value : [];
  const patterns = [];
  for (const item of list) {
    const parsed = parseShiftText(item);
    if (!parsed.ok) continue;
    patterns.push(`${formatMinutes(parsed.startMinutes)}-${formatMinutes(parsed.endMinutes)}`);
  }
  return patterns;
}

/**
 * Does this rule's clock guidance apply to the shift in front of us?
 *
 * True when the rule names no patterns at all - the common case, and the one
 * that keeps a blanket rule blanket. When it does name patterns, only a shift
 * matching one of them is governed by its clock times; every other shift falls
 * back to the ordinary shift ladder.
 *
 * Exclusions, forced lunches and group overrides are deliberately *not* scoped
 * this way: those are statements about the person, not about one shift.
 */
export function ruleAppliesToShift(rule, shift) {
  const patterns = rule?.appliesToShifts;
  if (!Array.isArray(patterns) || patterns.length === 0) return true;
  if (!shift || !Number.isFinite(shift.startMinutes) || !Number.isFinite(shift.endMinutes)) {
    return false;
  }
  const actual = `${formatMinutes(shift.startMinutes)}-${formatMinutes(shift.endMinutes)}`;
  return patterns.includes(actual);
}

/* -------------------------------------------------------------------------- */
/* Aliases                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * The alias book maps employee ids to the short display name used in Discord
 * exports, plus a list of tokens seen in historical messages that we could not
 * confidently match to anybody ("Cags").
 */
export function createAliasBook(entries = {}, unresolved = []) {
  return {
    entries: { ...entries },
    unresolved: unresolved.map((item) =>
      typeof item === 'string'
        ? { token: item, suggestions: [], resolvedTo: null, note: '' }
        : {
            token: item.token,
            suggestions: item.suggestions ?? [],
            resolvedTo: item.resolvedTo ?? null,
            note: item.note ?? '',
          },
    ),
  };
}

/** Display name for an employee: explicit alias, else first name, else id. */
export function displayNameFor(aliasBook, employeeId, fullName = '') {
  const alias = aliasBook?.entries?.[employeeId];
  if (alias) return alias;
  const first = firstNameOf(fullName);
  return first || employeeId;
}

/* -------------------------------------------------------------------------- */
/* Assignment & Schedule                                                      */
/* -------------------------------------------------------------------------- */

export const ASSIGNMENT_SOURCES = Object.freeze({
  AUTO: 'auto',
  MANUAL: 'manual',
  IMPORTED: 'imported',
});

/** One person's lunch on one day. */
export function createAssignment(partial = {}) {
  return {
    employeeId: partial.employeeId,
    name: partial.name ?? '',
    dayKey: partial.dayKey,
    /** Slot start, in minutes from midnight of that calendar day. */
    slotMinutes: partial.slotMinutes ?? null,
    durationMinutes: partial.durationMinutes ?? 30,
    /** 'day' | 'evening' | 'night'. */
    group: partial.group ?? 'day',
    /** Locked assignments survive regeneration untouched. */
    locked: partial.locked === true,
    /** True once a human has dragged or edited this assignment. */
    manual: partial.manual === true,
    source: partial.source ?? ASSIGNMENT_SOURCES.AUTO,
    /** Structured reasoning produced by the scheduler; see explain.js. */
    explanation: partial.explanation ?? null,
    score: partial.score ?? null,
    /** Reason text captured when a manager overrides a warning. */
    overrideReason: partial.overrideReason ?? '',
    /** Warning codes acknowledged for this assignment. */
    acknowledgedWarnings: partial.acknowledgedWarnings ?? [],
  };
}

/** Create an empty schedule shell for a week. */
export function createSchedule(weekCommencing) {
  const days = {};
  for (const dayKey of DAY_KEYS) {
    days[dayKey] = {
      dayKey,
      date: weekCommencing ? dateForDayKey(weekCommencing, dayKey) : '',
      assignments: [],
      unassigned: [],
      coverage: [],
      warnings: [],
      solved: true,
    };
  }
  return {
    schemaVersion: 1,
    /** Set by the scheduler; zero marks a repaired schedule from an older build. */
    rulesVersion: 0,
    weekCommencing,
    generatedAt: null,
    days,
    warnings: [],
    stats: null,
  };
}

/** All assignments across the week as a flat array. */
export function allAssignments(schedule) {
  if (!schedule?.days) return [];
  return DAY_KEYS.flatMap((dayKey) => schedule.days[dayKey]?.assignments ?? []);
}

/** Find one assignment by day + employee. */
export function findAssignment(schedule, dayKey, employeeId) {
  return (schedule?.days?.[dayKey]?.assignments ?? []).find(
    (assignment) => assignment.employeeId === employeeId,
  ) ?? null;
}

/** Group a day's assignments by slot, sorted by time then display name. */
export function groupBySlot(assignments) {
  const bySlot = new Map();
  for (const assignment of assignments) {
    if (assignment.slotMinutes === null || assignment.slotMinutes === undefined) continue;
    if (!bySlot.has(assignment.slotMinutes)) bySlot.set(assignment.slotMinutes, []);
    bySlot.get(assignment.slotMinutes).push(assignment);
  }
  return [...bySlot.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([slotMinutes, items]) => ({ slotMinutes, assignments: items }));
}
